import { useState, useMemo, useEffect } from 'react';
import {
  PackageCheck,
  Search,
  Loader2,
  Clock,
  AlertTriangle,
  Calendar,
  Truck,
  Package,
  MoreHorizontal,
  Eye,
  FileText,
  Plus,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';
import { useProducts } from '@/hooks/useProducts';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { subscribeToPurchaseOrders, unsubscribe } from '@/lib/realtime';

interface PurchaseOrder {
  id: string;
  location_id: string;
  supplier_id: string | null;
  supplier_location_id?: string | null;
  order_number: string | null;
  status: 'draft' | 'placed' | 'delivered' | 'partial' | 'received' | 'cancelled' | 'rejected';
  expected_date: string | null;
  received_date: string | null;
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  created_at: string;
  supplier?: { id: string; name: string; email?: string } | null;
  location?: { id: string; name: string } | null;
}

interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  product_id: string | null;
  raw_item_id: string | null;
  description: string | null;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  product?: { id: string; name: string; sku: string | null } | null;
  raw_item?: { id: string; name: string } | null;
}

interface Supplier {
  id: string;
  name: string;
  email?: string;
}

interface QuickReceiptItem {
  product_id: string;
  quantity_foh: string;
  quantity_boh: string;
  unit_cost: string;
}

export default function Receiving() {
  const { organization } = useAuth();
  const { currentLocation, locations } = useLocations();
  const { products } = useProducts();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  // Real-time subscription for purchase order updates (receiving page)
  useEffect(() => {
    if (!organization?.id) return;
    const channel = subscribeToPurchaseOrders(organization.id, () => {
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-orders'] });
    });
    return () => { unsubscribe(channel); };
  }, [organization?.id, queryClient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  
  // Receive dialog
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [selectedPOItems, setSelectedPOItems] = useState<PurchaseOrderItem[]>([]);
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, string>>({});
  
  // Quick receipt dialog (direct goods receipt without PO)
  const [quickReceiptDialogOpen, setQuickReceiptDialogOpen] = useState(false);
  const [quickReceiptData, setQuickReceiptData] = useState({
    supplier_id: '',
    invoice_number: '',
    notes: '',
  });
  const [quickReceiptItems, setQuickReceiptItems] = useState<QuickReceiptItem[]>([
    { product_id: '', quantity_foh: '', quantity_boh: '', unit_cost: '' },
  ]);
  const [quickReceiptError, setQuickReceiptError] = useState<string | null>(null);

  // Fetch suppliers
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name, email')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Supplier[];
    },
    enabled: !!organization?.id,
  });

  // Fetch pending purchase orders (sent or partial)
  const { data: purchaseOrders = [], isLoading } = useQuery({
    queryKey: ['pending-purchase-orders', currentLocation?.id],
    queryFn: async () => {
      let query = supabase
        .from('purchase_orders')
        .select(`
          *,
          supplier:suppliers(id, name, email),
          location:locations(id, name)
        `)
        .in('status', ['placed', 'delivered', 'partial'])
        .order('expected_date', { ascending: true, nullsFirst: false });
      
      if (currentLocation?.id) {
        query = query.eq('location_id', currentLocation.id);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as PurchaseOrder[];
    },
  });

  // Fetch PO items
  const fetchPOItems = async (poId: string) => {
    const { data, error } = await supabase
      .from('purchase_order_items')
      .select(`
        *,
        product:products(id, name, sku),
        raw_item:raw_inventory_items(id, name)
      `)
      .eq('purchase_order_id', poId);
    if (error) throw error;
    return data as PurchaseOrderItem[];
  };

  const fetchLinkedTransfer = async (poId: string) => {
    const { data, error } = await supabase
      .from('goods_transfers')
      .select('id, status')
      .eq('purchase_order_id', poId)
      .maybeSingle();
    if (error) throw error;
    return data as { id: string; status: string | null } | null;
  };

  const fetchTransferItems = async (transferId: string) => {
    const { data, error } = await supabase
      .from('goods_transfer_items')
      .select('id, product_id, quantity_sent, quantity_received')
      .eq('transfer_id', transferId);
    if (error) throw error;
    return (data || []) as Array<{
      id: string;
      product_id: string | null;
      quantity_sent: number;
      quantity_received: number | null;
    }>;
  };

  // Receive items mutation
  const receiveItems = useMutation({
    mutationFn: async ({ poId, items, locationId }: { poId: string; items: { id: string; quantity_received: number }[]; locationId: string }) => {
      const previousStatus = selectedPO?.status;
      const isInternal = !!selectedPO?.supplier_location_id;

      if (isInternal) {
        const linkedTransfer = await fetchLinkedTransfer(poId);
        if (!linkedTransfer) throw new Error('No linked transfer found for this order');
        if (linkedTransfer.status === 'pending') {
          throw new Error('This internal order has not been shipped yet');
        }

        const transferItems = await fetchTransferItems(linkedTransfer.id);
        const transferByProductId = new Map(
          transferItems
            .filter((ti) => !!ti.product_id)
            .map((ti) => [ti.product_id as string, ti] as const)
        );

        const newTotalByProductId = new Map<string, number>();
        const receiptDeltaByProductId = new Map<string, number>();
        const poItemUpdates: Array<{ po_item_id: string; quantity_received: number }> = [];

        items.forEach(({ id: poItemId, quantity_received: receivedNow }) => {
          if (receivedNow <= 0) return;

          const poItem = selectedPOItems.find((poi) => poi.id === poItemId);
          const productId = poItem?.product_id;
          if (!productId) return;

          const transferItem = transferByProductId.get(productId);
          if (!transferItem) {
            throw new Error('Missing transfer line item for one or more PO items');
          }

          const existingReceived = transferItem.quantity_received ?? 0;
          const newTotalReceived = existingReceived + receivedNow;

          newTotalByProductId.set(productId, newTotalReceived);
          receiptDeltaByProductId.set(productId, receivedNow);
          poItemUpdates.push({ po_item_id: poItemId, quantity_received: newTotalReceived });
        });

        // The transfer RPC receives the complete, absolute receipt state. This
        // prevents a partial PO receipt from accidentally omitting other lines.
        const payloadItems = transferItems.map((transferItem) => ({
          item_id: transferItem.id,
          quantity_received: transferItem.product_id && newTotalByProductId.has(transferItem.product_id)
            ? newTotalByProductId.get(transferItem.product_id) || 0
            : transferItem.quantity_received ?? 0,
        }));

        if (receiptDeltaByProductId.size > 0) {
          const { error: receiveError } = await supabase.rpc('receive_goods_transfer_items', {
            p_transfer_id: linkedTransfer.id,
            p_items: payloadItems,
          });
          if (receiveError) throw receiveError;

          const updateResults = await Promise.all(
            poItemUpdates.map(({ po_item_id, quantity_received }) =>
              supabase
                .from('purchase_order_items')
                .update({ quantity_received })
                .eq('id', po_item_id)
            )
          );
          const updateError = updateResults.find((r) => r.error)?.error;
          if (updateError) throw updateError;
        }

        const totalReceivedFor = (productId: string | null) => {
          if (!productId) return 0;
          if (newTotalByProductId.has(productId)) return newTotalByProductId.get(productId) || 0;
          return transferByProductId.get(productId)?.quantity_received ?? 0;
        };

        const allReceived = selectedPOItems.every((poi) => {
          const totalReceived = totalReceivedFor(poi.product_id);
          return totalReceived >= poi.quantity_ordered;
        });

        const someReceived = selectedPOItems.some((poi) => {
          const totalReceived = totalReceivedFor(poi.product_id);
          return totalReceived > 0;
        });

        const newStatus = allReceived ? 'received' : someReceived ? 'partial' : (previousStatus || 'delivered');
        await supabase
          .from('purchase_orders')
          .update({
            status: newStatus,
            received_date: allReceived ? new Date().toISOString() : null,
          })
          .eq('id', poId);

        if (newStatus === 'partial' && previousStatus !== 'partial') {
          try {
            const { error } = await supabase.functions.invoke('notify-po-partial-receive', {
              body: { purchaseOrderId: poId },
            });
            if (error) {
              // Do not fail the receipt flow if the email fails
              console.error('Partial receive email failed:', error);
            }
          } catch (err) {
            console.error('Partial receive email error:', err);
          }
        }

        return;
      }

      // Call receive_po_item() for each item
      for (const item of items) {
        if (item.quantity_received > 0) {
          const { error } = await supabase.rpc('receive_po_item', {
            p_po_item_id: item.id,
            p_quantity_received: item.quantity_received,
            p_location_id: locationId,
          });
          if (error) throw error;
        }
      }

      // Check if all items are fully received
      const allReceived = selectedPOItems.every(poi => {
        const newQty = parseFloat(receiveQuantities[poi.id] || '0');
        const totalReceived = poi.quantity_received + newQty;
        return totalReceived >= poi.quantity_ordered;
      });

      const someReceived = selectedPOItems.some(poi => {
        const newQty = parseFloat(receiveQuantities[poi.id] || '0');
        const totalReceived = poi.quantity_received + newQty;
        return totalReceived > 0;
      });

      // Update PO status
      const newStatus = allReceived ? 'received' : someReceived ? 'partial' : 'placed';
      await supabase
        .from('purchase_orders')
        .update({ 
          status: newStatus,
          received_date: allReceived ? new Date().toISOString() : null,
        })
        .eq('id', poId);

      if (newStatus === 'partial' && previousStatus !== 'partial') {
        try {
          const { error } = await supabase.functions.invoke('notify-po-partial-receive', {
            body: { purchaseOrderId: poId },
          });
          if (error) {
            // Do not fail the receipt flow if the email fails
            console.error('Partial receive email failed:', error);
          }
        } catch (err) {
          console.error('Partial receive email error:', err);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['goods-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Items received', { description: 'Inventory has been updated' });
      setReceiveDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Error', { description: error.message });
    },
  });

  // Quick receipt mutation - creates invoice and updates inventory directly
  const createQuickReceipt = useMutation({
    mutationFn: async () => {
      if (!currentLocation?.id) throw new Error('No location selected');

      const validItems = quickReceiptItems.filter(item => item.product_id || item.quantity_foh || item.quantity_boh);
      if (validItems.length === 0) throw new Error('Please add at least one item with quantity');

      const receiptItems = validItems.map(item => ({
        product_id: item.product_id,
        quantity_foh: Number(item.quantity_foh || 0),
        quantity_boh: Number(item.quantity_boh || 0),
        unit_cost: Number(item.unit_cost || 0),
      }));

      if (receiptItems.some(item =>
        !item.product_id ||
        !Number.isFinite(item.quantity_foh) ||
        !Number.isFinite(item.quantity_boh) ||
        !Number.isFinite(item.unit_cost) ||
        item.quantity_foh < 0 ||
        item.quantity_boh < 0 ||
        item.unit_cost < 0 ||
        item.quantity_foh + item.quantity_boh <= 0
      )) {
        throw new Error('Each receipt line needs a product, positive quantity, and non-negative cost');
      }

      if (new Set(receiptItems.map(item => item.product_id)).size !== receiptItems.length) {
        throw new Error('A product can appear only once in a direct receipt');
      }

      const { data: invoiceId, error } = await supabase.rpc('create_direct_inventory_receipt', {
        p_location_id: currentLocation.id,
        p_supplier_id: quickReceiptData.supplier_id || null,
        p_invoice_number: quickReceiptData.invoice_number || null,
        p_notes: quickReceiptData.notes || null,
        p_items: receiptItems,
      });

      if (error) throw error;
      return invoiceId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Goods received', { description: 'Inventory has been updated and invoice created' });
      setQuickReceiptDialogOpen(false);
      resetQuickReceiptForm();
    },
    onError: (error) => {
      setQuickReceiptError(error.message);
    },
  });

  // Filter POs
  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter(po => {
      // Status filter
      if (statusFilter === 'pending' && !['placed', 'delivered', 'partial'].includes(po.status)) return false;
      if (statusFilter === 'overdue') {
        if (!po.expected_date) return false;
        if (new Date(po.expected_date) > new Date()) return false;
        if (!['placed', 'delivered', 'partial'].includes(po.status)) return false;
      }
      if (statusFilter === 'today') {
        if (!po.expected_date) return false;
        const today = new Date().toDateString();
        if (new Date(po.expected_date).toDateString() !== today) return false;
      }

      // Supplier filter
      if (supplierFilter !== 'all' && po.supplier_id !== supplierFilter) return false;

      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const supplierName = po.supplier?.name || '';
        const internalSupplierName = po.supplier_location_id
          ? locations.find(l => l.id === po.supplier_location_id)?.name || ''
          : '';
        const deliverToName = po.location?.name || '';
        return (
          po.order_number?.toLowerCase().includes(query) ||
          supplierName.toLowerCase().includes(query) ||
          internalSupplierName.toLowerCase().includes(query) ||
          deliverToName.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [purchaseOrders, statusFilter, supplierFilter, searchQuery, locations]);

  // Stats
  const stats = useMemo(() => {
    const today = new Date().toDateString();
    return {
      pending: purchaseOrders.filter(po => ['placed', 'delivered', 'partial'].includes(po.status)).length,
      overdue: purchaseOrders.filter(po =>
        po.expected_date &&
        new Date(po.expected_date) < new Date() &&
        ['placed', 'delivered', 'partial'].includes(po.status)
      ).length,
      expectedToday: purchaseOrders.filter(po =>
        po.expected_date &&
        new Date(po.expected_date).toDateString() === today
      ).length,
      totalValue: purchaseOrders
        .filter(po => ['placed', 'delivered', 'partial'].includes(po.status))
        .reduce((sum, po) => sum + (po.total || 0), 0),
    };
  }, [purchaseOrders]);

  const handleReceiveItems = async () => {
    if (!selectedPO) return;

    const itemsToReceive = selectedPOItems
      .filter(poi => receiveQuantities[poi.id])
      .map(poi => ({
        id: poi.id,
        quantity_received: parseFloat(receiveQuantities[poi.id]) || 0,
      }));

    await receiveItems.mutateAsync({ 
      poId: selectedPO.id, 
      items: itemsToReceive,
      locationId: selectedPO.location_id,
    });
  };

  const openReceiveDialog = async (po: PurchaseOrder) => {
    setSelectedPO(po);
    const items = await fetchPOItems(po.id);
    setSelectedPOItems(items);
    // Initialize receive quantities to empty
    const initialQuantities: Record<string, string> = {};
    items.forEach(item => {
      initialQuantities[item.id] = '';
    });
    setReceiveQuantities(initialQuantities);
    setReceiveDialogOpen(true);
  };

  const resetQuickReceiptForm = () => {
    setQuickReceiptData({ supplier_id: '', invoice_number: '', notes: '' });
    setQuickReceiptItems([{ product_id: '', quantity_foh: '', quantity_boh: '', unit_cost: '' }]);
    setQuickReceiptError(null);
  };

  const addQuickReceiptItem = () => {
    setQuickReceiptItems([...quickReceiptItems, { product_id: '', quantity_foh: '', quantity_boh: '', unit_cost: '' }]);
  };

  const removeQuickReceiptItem = (index: number) => {
    if (quickReceiptItems.length > 1) {
      setQuickReceiptItems(quickReceiptItems.filter((_, i) => i !== index));
    }
  };

  const updateQuickReceiptItem = (index: number, field: string, value: string) => {
    const newItems = [...quickReceiptItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setQuickReceiptItems(newItems);
  };

  const isOverdue = (date: string | null) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const isToday = (date: string | null) => {
    if (!date) return false;
    return new Date(date).toDateString() === new Date().toDateString();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold">Receiving</h2>
          <p className="text-sm text-muted-foreground">
            Receive goods from purchase orders or record direct receipts
          </p>
        </div>
        <Button variant="blaze" className="gap-2" onClick={() => setQuickReceiptDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Quick Receipt
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setStatusFilter('pending')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs text-muted-foreground">Awaiting Delivery</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(
          "cursor-pointer hover:bg-muted/30 transition-colors",
          stats.overdue > 0 && "border-red-200 dark:border-red-900"
        )} onClick={() => setStatusFilter('overdue')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                <p className="text-xs text-muted-foreground">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setStatusFilter('today')}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.expectedToday}</p>
                <p className="text-xs text-muted-foreground">Expected Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Package className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</p>
                <p className="text-xs text-muted-foreground">Pending Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex-1 min-w-[200px] max-w-sm">
          <Label className="mb-1.5 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Order #, supplier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-w-[150px]">
          <Label className="mb-1.5 block">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="today">Expected Today</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[180px]">
          <Label className="mb-1.5 block">Supplier</Label>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All suppliers</SelectItem>
              {suppliers.map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pending Deliveries List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading pending deliveries...</span>
          </div>
        </div>
      ) : filteredPOs.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <PackageCheck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium mb-2">No pending deliveries</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {statusFilter === 'overdue' 
                  ? 'No overdue deliveries - great job!' 
                  : 'All purchase orders have been received'}
              </p>
              <Button variant="outline" onClick={() => setQuickReceiptDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Quick Receipt (No PO)
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPOs.map((po) => (
            <Card 
              key={po.id} 
              className={cn(
                "hover:bg-muted/30 transition-colors",
                isOverdue(po.expected_date) && "border-red-200 dark:border-red-800",
                isToday(po.expected_date) && "border-green-200 dark:border-green-800"
              )}
            >
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className={cn(
                    "p-3 rounded-lg",
                    isOverdue(po.expected_date) 
                      ? "bg-red-100 dark:bg-red-900/30" 
                      : isToday(po.expected_date)
                        ? "bg-green-100 dark:bg-green-900/30"
                        : "bg-muted"
                  )}>
                    <Truck className={cn(
                      "h-5 w-5",
                      isOverdue(po.expected_date) 
                        ? "text-red-600 dark:text-red-400" 
                        : isToday(po.expected_date)
                          ? "text-green-600 dark:text-green-400"
                          : "text-muted-foreground"
                    )} />
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{po.order_number || 'Draft'}</span>
                      <Badge variant={po.status === 'partial' ? 'secondary' : 'outline'} className="text-xs">
                        {po.status === 'partial' ? 'Partially Received' : 'Awaiting'}
                      </Badge>
                      {isOverdue(po.expected_date) && (
                        <Badge variant="destructive" className="text-xs">Overdue</Badge>
                      )}
                      {isToday(po.expected_date) && !isOverdue(po.expected_date) && (
                        <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          Due Today
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>
                        {po.supplier_location_id
                          ? `Internal: ${locations.find(l => l.id === po.supplier_location_id)?.name || 'Unknown location'}`
                          : (po.supplier?.name || 'No supplier')}
                      </span>
                      <span>•</span>
                      <span>{po.location?.name}</span>
                      {po.expected_date && (
                        <>
                          <span>•</span>
                          <span>Expected: {new Date(po.expected_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Value */}
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(po.total)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(po.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="blaze" 
                      size="sm"
                      onClick={() => openReceiveDialog(po)}
                    >
                      <PackageCheck className="h-4 w-4 mr-1" />
                      Receive
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openReceiveDialog(po)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View & Receive
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <FileText className="h-4 w-4 mr-2" />
                          View PO Details
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Receive Items Dialog */}
      <ResponsiveModal open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <ResponsiveModalContent maxWidth="md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Receive Items</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Enter quantities being received now for {selectedPO?.order_number}
              {selectedPO?.supplier_location_id
                ? ` from ${locations.find(l => l.id === selectedPO.supplier_location_id)?.name || 'Unknown location'}`
                : selectedPO?.supplier?.name
                  ? ` from ${selectedPO.supplier.name}`
                  : ''}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-3">
              {selectedPOItems.map((item) => {
                const remaining = item.quantity_ordered - item.quantity_received;
                const isFullyReceived = remaining <= 0;
                const itemName = item.product?.name || item.raw_item?.name || item.description || 'Unknown Item';
                return (
                  <div key={item.id} className={cn(
                    "flex items-center gap-4 p-3 rounded-lg border",
                    isFullyReceived && "bg-muted/50 opacity-60"
                  )}>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{itemName}</p>
                      <p className="text-xs text-muted-foreground">
                        Ordered: {item.quantity_ordered} • Received: {item.quantity_received} • 
                        <span className={remaining > 0 ? "text-amber-600 font-medium" : "text-green-600"}>
                          {remaining > 0 ? ` Remaining: ${remaining}` : " Fully received"}
                        </span>
                      </p>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        min="0"
                        max={remaining}
                        value={receiveQuantities[item.id] || ''}
                        onChange={(e) => setReceiveQuantities({
                          ...receiveQuantities,
                          [item.id]: e.target.value,
                        })}
                        placeholder={isFullyReceived ? "Done" : `0-${remaining}`}
                        disabled={isFullyReceived}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleReceiveItems}
              disabled={receiveItems.isPending}
            >
              {receiveItems.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirm Receipt
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Quick Receipt Dialog */}
      <ResponsiveModal open={quickReceiptDialogOpen} onOpenChange={setQuickReceiptDialogOpen}>
        <ResponsiveModalContent maxWidth="lg">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Quick Receipt (No PO)</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Record goods received without a purchase order. Creates an invoice automatically.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody className="max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              {quickReceiptError && (
                <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  {quickReceiptError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier (optional)</Label>
                  <Select
                    value={quickReceiptData.supplier_id}
                    onValueChange={(value) => setQuickReceiptData({ ...quickReceiptData, supplier_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={quickReceiptData.invoice_number}
                    onChange={(e) => setQuickReceiptData({ ...quickReceiptData, invoice_number: e.target.value })}
                    placeholder="INV-001"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={quickReceiptData.notes}
                  onChange={(e) => setQuickReceiptData({ ...quickReceiptData, notes: e.target.value })}
                  placeholder="Any notes about this receipt..."
                  rows={2}
                />
              </div>

              {/* Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items Received</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addQuickReceiptItem}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Item
                  </Button>
                </div>

                {/* Column Headers */}
                <div className="flex items-center gap-2 px-3 text-xs font-medium text-muted-foreground">
                  <div className="flex-1">Product</div>
                  <div className="w-20 text-center">FOH Qty</div>
                  <div className="w-20 text-center">BOH Qty</div>
                  <div className="w-24 text-center">Unit Cost</div>
                  <div className="w-8" />
                </div>

                <div className="space-y-2">
                  {quickReceiptItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 rounded-lg border">
                      <div className="flex-1">
                        <Select
                          value={item.product_id}
                          onValueChange={(value) => updateQuickReceiptItem(index, 'product_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select product" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((product) => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity_foh}
                          onChange={(e) => updateQuickReceiptItem(index, 'quantity_foh', e.target.value)}
                          placeholder="FOH"
                          className="text-center"
                        />
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          min="0"
                          value={item.quantity_boh}
                          onChange={(e) => updateQuickReceiptItem(index, 'quantity_boh', e.target.value)}
                          placeholder="BOH"
                          className="text-center"
                        />
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_cost}
                          onChange={(e) => updateQuickReceiptItem(index, 'unit_cost', e.target.value)}
                          placeholder="Cost"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-red-600"
                        onClick={() => removeQuickReceiptItem(index)}
                        disabled={quickReceiptItems.length === 1}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="flex justify-end pt-2 border-t">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-lg font-semibold">
                      {formatCurrency(
                        quickReceiptItems.reduce((sum, item) => {
                          const qtyFoh = parseFloat(item.quantity_foh) || 0;
                          const qtyBoh = parseFloat(item.quantity_boh) || 0;
                          const cost = parseFloat(item.unit_cost) || 0;
                          return sum + (qtyFoh + qtyBoh) * cost;
                        }, 0)
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => {
              setQuickReceiptDialogOpen(false);
              resetQuickReceiptForm();
            }}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={() => createQuickReceipt.mutate()}
              disabled={createQuickReceipt.isPending}
            >
              {createQuickReceipt.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Record Receipt
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
